import { ReactNode, useState, useRef, useCallback, useEffect } from 'react';
import { ChatInterface } from './ChatInterface';
import { RightPanel } from './RightPanel';

interface SplitLayoutProps {
  children?: ReactNode;
}

const SPLIT_KEY = 'athen-split-ratio';
const DEFAULT_RATIO = 50; // right panel percentage
const MIN_RATIO = 25;
const MAX_RATIO = 70;

export function SplitLayout({ children }: SplitLayoutProps) {
  const [rightPanelPercent, setRightPanelPercent] = useState(() => {
    try {
      const saved = localStorage.getItem(SPLIT_KEY);
      if (saved) {
        const val = parseFloat(saved);
        if (val >= MIN_RATIO && val <= MAX_RATIO) return val;
      }
    } catch {}
    return DEFAULT_RATIO;
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  useEffect(() => {
    try { localStorage.setItem(SPLIT_KEY, String(rightPanelPercent)); } catch {}
  }, [rightPanelPercent]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percent = 100 - (x / rect.width) * 100;
      setRightPanelPercent(Math.min(MAX_RATIO, Math.max(MIN_RATIO, percent)));
    };

    const handleMouseUp = () => {
      if (dragging.current) {
        dragging.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const leftPercent = 100 - rightPanelPercent;

  return (
    <div ref={containerRef} className="h-[calc(100vh-12rem)] flex">
      {/* Left Panel: Chat */}
      <div className="min-w-0 h-full" style={{ width: `${leftPercent}%` }}>
        {children || <ChatInterface />}
      </div>

      {/* Draggable Divider */}
      <div
        onMouseDown={handleMouseDown}
        className="w-3 flex-shrink-0 cursor-col-resize group flex items-center justify-center hover:bg-primary-50 transition-colors relative"
      >
        <div className="w-0.5 h-8 rounded-full bg-neutral-300 group-hover:bg-primary-400 transition-colors" />
      </div>

      {/* Right Panel: Tabs */}
      <div className="min-w-[350px] h-full" style={{ width: `${rightPanelPercent}%` }}>
        <RightPanel />
      </div>
    </div>
  );
}
